import majorCities from '~/static/major-cities'
import { IGeoPoint, GeoPoint } from './GeoPoint'
import { Media } from './Media'
import { MAP_TYPES, MAP_TYPES_CATEGORIES } from '~/static/map'
import { SuggestionJSONContext } from '@mapbox/search-js-core/dist/searchbox/types'

export const PlaceTagsTypes = [ 'amusement_park', 'aquarium', 'art_gallery', 'bakery', 'bar', 'book_store', 'bowling_alley', 'cafe', 'library', 'movie_theater', 'museum', 'night_club', 'park', 'restaurant', 'shopping_mall', 'store', 'tourist_attraction', 'zoo', 'health', 'doctor', 'non-profit organisation', 'spa' ] as const

export const PlaceTagsPrices = [ 'price-free', 'price-cheap', 'price-moderate', 'price-expensive', 'price-luxury' ] as const

export const PlaceTagsSize = [ 'reservable', 'nonReservable', 'size-takeout', 'size-tiny', 'size-small', 'size-big', 'size-huge' ] as const

export const PlaceTagsDetails = [ 'accessible', 'nonAccessible','lgbtqOwned', 'lgbtqOnly', 'womanOwner', 'vegetarian', 'vegan' ] as const

export const PlaceTags = [
  ...PlaceTagsTypes,
  ...PlaceTagsPrices,
  ...PlaceTagsSize,
  ...PlaceTagsDetails
] as const

export interface Address {
  name: string
  context: SuggestionJSONContext
  coordinates: [number, number]
}
export interface PlaceReview {
  _id: string
  
  status: 'pending' | 'published'
  comment: string
  recommend: '' | 'yes' | 'no' | 'maybe'
  events: '' | 'yes' | 'no' | 'maybe'
  only: '' | 'yes' | 'no' | 'maybe'
  feeling: '' | 'yes' | 'no'
  symbols: '' | 'yes' | 'no'
  rating: number
  isAnonymous: boolean
  
  owner: string
  createdAt: Date
}

export type IPlace = {
  _id: string
  id: string
  name: string
  type: keyof typeof MAP_TYPES
  
  summary: string
  description: string

  slug: string
  address: string
  addressComponents: {
    city: string
    state: string
    country: string
    postalCode: string
    street: string
    streetNumber: string
    countryCode: string
  }
  isDiscover: boolean
  isSponsored: boolean
  isCertified: boolean
  isVerified: boolean
  isLGBTQ: boolean
  
  tags: typeof PlaceTags[number][]
  types: string[]

  offers: {
    title: string
    summary: string
    description: string
    secret: string
  }[]
  
  urls: {
    maps?: string
    website?: string
  }

  score: number

  photos: string[]
  medias: Media[]

  followerCount: number
  eventsCount: number

  geolocation: GeoPoint
  location: keyof typeof majorCities

  page: IPlace | null

  createdAt: Date
  organization: string | null

  // VIRTUALS
  rating: number
  reviewsVisible: PlaceReview[]
  reviewCount: number
  hasSaved?: boolean
  saved: string[]
}

export interface Place extends IPlace {}

export class Place implements IPlace {
  coordinates: [ number, number ] | null
  geolocation: GeoPoint
  
  constructor(props: IPlace) {
    Object.assign(this, {
      ...props,
      medias: (props.medias ?? []).filter(m => m.medias?.[0]?.src && !m.medias?.[0]?.src.includes('googleapi')).map(m => new Media(m)),

      geolocation: new GeoPoint(props.geolocation),
      coordinates: new GeoPoint(props.geolocation).coordinates,
      createdAt: props.createdAt ? new Date(props.createdAt) : new Date(),
      reviewsVisible: (props.reviewsVisible ?? []).map(r => ({
        ...r,
        createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
      })),

      // PAGE
      page: props.page ? new Place(props.page) : null,
      summary: (stripTags(props.summary ?? '')).length > 0 ? props.summary : (props.page?.summary ?? ''),
      description: (stripTags(props.description ?? '')).length > 0 ? props.description : (props.page?.description ?? ''),
      offers: (props.offers ?? []).length > 0 ? props.offers : (props.page?.offers ?? []),
    })
  }

  public hasLiked (userId?: string) {

    return (this.saved ?? []).includes(userId ?? '')
  }

  public get country () {
    return this.addressComponents.country ?? 'unknown'
  }

  public get isRatingEnabled () {
    const tags = [ ...(this.tags ?? []), ...(this.types ?? []) ]

    return tags.includes('restaurant') || tags.includes('bar') || tags.includes('night_club') || tags.includes('amusement_park') || tags.includes('cafe') || tags.includes('night_club') || tags.includes('museum')
  }

  public hasReviewed (userId?: string) {
    return this.reviewsVisible.find(r => r.owner === userId)
  }

  public review (userId?: string) {
    return this.reviewsVisible.find(r => r.owner === userId) ?? null
  }

  public get mapType () {
    return (this.type ?? (Object.entries(MAP_TYPES).find(([ key, value ]) => {
      return value.types.some(t => this.types.includes(t))
    })?.[0] ?? 'bar')) as keyof typeof MAP_TYPES
  }

  public get mapCategory () {
    return (Object.entries(MAP_TYPES_CATEGORIES).find(([ key, value ]) => {
      return value.types.includes(this.mapType)
    })?.[0] ?? 'restauration') as keyof typeof MAP_TYPES_CATEGORIES
  }

  public get marker () {
    return {
      id: this.mapType ? 'marker-' + this.mapType + (this.isLGBTQ ? '-lgbt' : '') + (this.isCertified ? '-cert' : '') : 'marker-bar',
      src: MAP_TYPES[this.mapType]?.icons?.[this.isLGBTQ && this.isCertified ? 'lgbtCert' : (this.isCertified ? 'cert' : (this.isLGBTQ ? 'lgbt' : 'default'))] ?? MAP_TYPES.bar.icons.default
    }
  }

  public get point () {
    return {
      id: 'point-' + this.mapCategory + (this.isLGBTQ ? '-lgbt' : ''),
      src: MAP_TYPES_CATEGORIES[this.mapCategory]?.icons?.[this.isLGBTQ ? 'lgbt' : 'default'] ?? MAP_TYPES_CATEGORIES.restauration.icons.default
    }
  }

  public get addressSimple () {
    if (!this.address) return ''

    let parts = this.address.split(',')

    return [ parts[0]?.trim(), parts[1]?.trim().split(' ')?.[1] ].filter(c => c).join(', ')
  }

  public get ratingScore () {
    return this.reviewCount >= 5 ? this.rating ?? 0 : 0
  }
}
